import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import i18n from "../i18n";
import { authApi, type MeResponse, type TwoFactorStatus, type UserSummary } from "../api/auth";
import { resetSessionExpiredNotice, setSessionExpiredHandler } from "../api/client";

// Fallback when the backend omits the block (older API / partial test mocks): treat as "no 2FA obligation".
const NO_TWO_FACTOR: TwoFactorStatus = {
  enabled: false,
  required: false,
  setupRequired: false,
  graceUntil: null,
};

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: UserSummary; permissions: Set<string>; twoFactor: TwoFactorStatus };

type AuthContextValue = {
  state: AuthState;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
    totpCode?: string,
  ) => Promise<{ mfaRequired: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  // Mirror of state.status for the session-expired handler — it runs outside
  // React's render cycle and must see the CURRENT status, not a stale closure.
  const statusRef = useRef<AuthState["status"]>("loading");
  useEffect(() => { statusRef.current = state.status; }, [state.status]);

  // When a 401 survives a failed token refresh, the session is gone: flip to
  // anonymous so route guards redirect to /login instead of leaving dead
  // widgets polling 401s. Only acts when we believed we were logged in.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (statusRef.current !== "authenticated") return;
      // Flag for the login page so it can explain WHY the user landed there.
      try { sessionStorage.setItem("imp.sessionExpired", "1"); } catch { /* ignore */ }
      setState({ status: "anonymous" });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me: MeResponse = await authApi.me();
      resetSessionExpiredNotice();
      setState({
        status: "authenticated",
        user: me.user,
        permissions: new Set(me.permissions),
        twoFactor: me.twoFactor ?? NO_TWO_FACTOR,
      });
      // Sync UI language to the user's preference (only when it actually changes
      // to avoid kicking off bundle reloads on every refresh).
      if (me.user.locale && i18n.resolvedLanguage !== me.user.locale) {
        void i18n.changeLanguage(me.user.locale);
      }
    } catch {
      setState({ status: "anonymous" });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean, totpCode?: string) => {
      const res = await authApi.login(email, password, rememberMe, totpCode);
      if (res && typeof res === "object" && "mfaRequired" in res && res.mfaRequired) {
        return { mfaRequired: true } as const;
      }
      await refresh();
      return { mfaRequired: false } as const;
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setState({ status: "anonymous" });
    }
  }, []);

  const hasPermission = useCallback(
    (permission: string) =>
      state.status === "authenticated" && state.permissions.has(permission),
    [state],
  );

  const hasRole = useCallback(
    (role: string) =>
      state.status === "authenticated" && state.user.roles.includes(role),
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refresh, hasPermission, hasRole }),
    [state, login, logout, refresh, hasPermission, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
